/**
 * Request Thread UI Utilities
 *
 * Deterministic logic for deciding form-first vs chat-first rendering.
 * No LLM involved - decision is based on requestSpec field types.
 */

export type UIMode = 'form' | 'chat';

export interface AllowedField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  description?: string;
  enumValues?: string[];
}

export interface RequestSpec {
  ask: string;
  description?: string;
  allowedFields: AllowedField[];
  uiMode?: 'form' | 'chat' | 'auto';
}

/**
 * Field types that are considered "structured" and work well in forms.
 * These are fields where form controls provide a better UX than free-text.
 */
const STRUCTURED_FIELD_TYPES = ['boolean', 'enum', 'enum_multi', 'number', 'date'];

/**
 * Determine whether to show form-first or chat-first UI.
 *
 * Heuristic:
 * 1. If explicit uiMode is set (not 'auto'), use it
 * 2. Otherwise, use form-first if:
 *    - Has defined fields AND
 *    - Has any structured fields (boolean, enum, enum_multi, number, date)
 *
 * The reasoning: If a request has any structured fields, users will benefit
 * from proper form controls rather than typing values in chat.
 *
 * @param requestSpec The request specification
 * @returns 'form' or 'chat'
 */
export function determineUIMode(requestSpec: RequestSpec | null | undefined): UIMode {
  // No spec means chat mode
  if (!requestSpec) {
    return 'chat';
  }

  // If explicit uiMode set (and not 'auto'), use it
  if (requestSpec.uiMode && requestSpec.uiMode !== 'auto') {
    return requestSpec.uiMode;
  }

  const fields = requestSpec.allowedFields || [];

  // No fields defined - use chat
  if (fields.length === 0) {
    return 'chat';
  }

  // Count structured vs unstructured fields
  const structuredCount = fields.filter((f) =>
    STRUCTURED_FIELD_TYPES.includes(f.type)
  ).length;

  // Form-first if we have ANY structured fields
  // This ensures boolean, enum, date etc. get proper form controls
  if (structuredCount > 0) {
    return 'form';
  }

  // All fields are freeform text - chat might be better for conversational flow
  // But still use form if there are multiple fields (easier to track)
  if (fields.length >= 2) {
    return 'form';
  }

  // Single text field - chat is fine
  return 'chat';
}

/**
 * Get field type category for styling/rendering decisions.
 */
export function getFieldTypeCategory(type: string): 'choice' | 'text' | 'file' | 'other' {
  switch (type) {
    case 'boolean':
    case 'enum':
    case 'enum_multi':
      return 'choice';
    case 'string':
    case 'string_long':
    case 'number':
    case 'date':
      return 'text';
    case 'file':
      return 'file';
    default:
      return 'other';
  }
}

/**
 * Check if a field value is considered "empty" for validation purposes.
 */
export function isFieldEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}
